import React, { useContext, useState } from "react";
import ThemeContext from "../lib/theme-context";
import Tooltip from "@mui/material/Tooltip";

const PositionVisualizer = ({
  direction,
  openPrice,
  stopPrice,
  stopLossPips,
  lots,
  units,
  moneyRisk,
  risk,
  depositCurrency,
  exchangeRate,
  exchangeLabel,
  contractSize,
  setContractSize,
  saveContractSize,
  customContractSize,
  setCustomContractSize,
  showContractDropdown,
  setShowContractDropdown,
  accountBalance,
  pair,
  width,
}) => {
  const theme = useContext(ThemeContext);
  const moneySymbol = ["$", "£"];
  const [copyNotification, setCopyNotification] = useState("");
  const [longPressTimer, setLongPressTimer] = useState(null);

  // Copy notification handler
  const showCopyNotification = (message) => {
    setCopyNotification(message);
    setTimeout(() => setCopyNotification(""), 2000);
  };

  // Long press handlers for contract badge
  const handleContractMouseDown = () => {
    const timer = setTimeout(() => {
      setShowContractDropdown(true);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleContractMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleContractMouseLeave = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Calculate the visual height ratio between entry and stop
  const getVisualHeight = () => {
    if (!openPrice || !stopPrice) return 50;
    const priceDiff = Math.abs(Number(openPrice) - Number(stopPrice));
    const avgPrice = (Number(openPrice) + Number(stopPrice)) / 2;
    const percentDiff = (priceDiff / avgPrice) * 100;
    // Scale it for visual purposes (min 20%, max 80%)
    return Math.min(Math.max(percentDiff * 10, 80), 100);
  };

  const visualHeight = getVisualHeight();
  const isLong = direction?.toLowerCase() === "long";
  const longColor = theme[0] === "dark" ? "#e5e7eb" : "#1f2937";

  return (
    <div
      style={{
        backgroundColor:
          theme[0] === "dark"
            ? "rgba(237, 158, 14, 0.1)"
            : "rgba(237, 158, 14, 0.05)",
        borderRadius: "12px",
        padding: width <= 800 ? "14px" : "16px",
        border:
          theme[0] === "dark"
            ? "1px solid rgba(237, 158, 14, 0.3)"
            : "1px solid rgba(237, 158, 14, 0.2)",
        position: "relative",
      }}
    >
      {/* Copy Notification Toast */}
      {copyNotification && (
        <div
          style={{
            position: "absolute",
            top: "-40px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#10b981",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "600",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            animation: "fadeIn 0.2s ease-in",
            whiteSpace: "nowrap",
          }}
        >
          ✓ {copyNotification}
        </div>
      )}

      {/* Visual Position Display */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "0",
          flexDirection: width <= 600 ? "column" : "row",
        }}
      >
        {/* Left side - Visual Chart */}
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            backgroundColor:
              theme[0] === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.03)",
            borderRadius: "8px",
            padding: "16px",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "280px",
              height: "180px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Stop Loss Line */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                order: isLong ? 2 : 0,
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "2px",
                  backgroundColor: "#ef4444",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "-6px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#ef4444",
                  }}
                />
                {/* SELL/BUY label on the stop line */}
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#ef4444",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    backgroundColor:
                      theme[0] === "dark" ? "#1a1a1a" : "#ffffff",
                    padding: "0 6px",
                  }}
                >
                  {isLong ? "SELL" : "BUY"}
                </span>
              </div>
              <div style={{ minWidth: "90px", textAlign: "right" }}>
                <p
                  style={{
                    fontSize: "9px",
                    fontWeight: "600",
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    margin: "0 0 2px 0",
                    textTransform: "uppercase",
                  }}
                >
                  Stop Loss
                </p>
                <p
                  onClick={() => {
                    if (stopPrice) {
                      navigator.clipboard.writeText(stopPrice.toString());
                      showCopyNotification(`Copied ${stopPrice} Stop`);
                    }
                  }}
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#ef4444",
                    margin: 0,
                    cursor: stopPrice ? "pointer" : "default",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    if (stopPrice) e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseOut={(e) => {
                    if (stopPrice) e.currentTarget.style.opacity = "1";
                  }}
                >
                  {stopPrice || "—"}
                </p>
              </div>
            </div>

            {/* Double-ended Arrow */}
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                margin: "12px 0",
                order: 1,
                position: "relative",
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                {/* Vertical line with arrows */}
                <div
                  style={{
                    position: "relative",
                    width: "2px",
                    height: `${visualHeight}%`,
                    backgroundColor: longColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Top arrow */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-6px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderBottom: `10px solid ${longColor}`,
                    }}
                  />
                  
                  {/* Bottom arrow */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-6px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderTop: `10px solid ${longColor}`,
                    }}
                  />

                  {/* Risk % label - left side */}
                  <div
                    onClick={() => {
                      if (risk) {
                        navigator.clipboard.writeText(risk.toString());
                        showCopyNotification(`Copied ${risk}%`);
                      }
                    }}
                    style={{
                      position: "absolute",
                      right: "24px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor:
                        theme[0] === "dark" ? "#1a1a1a" : "#ffffff",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      border: `1.5px solid ${longColor}`,
                      whiteSpace: "nowrap",
                      cursor: risk ? "pointer" : "default",
                      transition: "opacity 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      if (risk) e.currentTarget.style.opacity = "0.8";
                    }}
                    onMouseOut={(e) => {
                      if (risk) e.currentTarget.style.opacity = "1";
                    }}
                  >
                    <p
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: longColor,
                        margin: 0,
                      }}
                    >
                      {risk}%
                    </p>
                  </div>

                  {/* Pips label - right side */}
                  <div
                    onClick={() => {
                      if (stopLossPips) {
                        navigator.clipboard.writeText(stopLossPips.toString());
                        showCopyNotification(`Copied ${stopLossPips} SL`);
                      }
                    }}
                    style={{
                      position: "absolute",
                      left: "24px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor:
                        theme[0] === "dark" ? "#1a1a1a" : "#ffffff",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      border: `1.5px solid ${longColor}`,
                      whiteSpace: "nowrap",
                      cursor: stopLossPips ? "pointer" : "default",
                      transition: "opacity 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      if (stopLossPips) e.currentTarget.style.opacity = "0.8";
                    }}
                    onMouseOut={(e) => {
                      if (stopLossPips) e.currentTarget.style.opacity = "1";
                    }}
                  >
                    <p
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: longColor,
                        margin: 0,
                      }}
                    >
                      {stopLossPips || "—"} pips
                    </p>
                  </div>
                </div>
              </div>
              <div style={{ minWidth: "90px" }} />
            </div>

            {/* Entry Line */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                order: isLong ? 0 : 2,
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "3px",
                  backgroundColor: "#ED9E0E",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "-8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#ED9E0E",
                    border: `3px solid ${theme[0] === "dark" ? "#1a1a1a" : "#ffffff"}`,
                  }}
                />
                {/* BUY/SELL label on the entry line */}
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#ED9E0E",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    backgroundColor:
                      theme[0] === "dark" ? "#1a1a1a" : "#ffffff",
                    padding: "0 6px",
                  }}
                >
                  {isLong ? "BUY" : "SELL"}
                </span>
              </div>
              <div style={{ minWidth: "90px", textAlign: "right" }}>
                <p
                  style={{
                    fontSize: "9px",
                    fontWeight: "600",
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    margin: "0 0 2px 0",
                    textTransform: "uppercase",
                  }}
                >
                  Entry
                </p>
                <p
                  onClick={() => {
                    if (openPrice) {
                      navigator.clipboard.writeText(openPrice.toString());
                      showCopyNotification(`Copied ${openPrice} Open`);
                    }
                  }}
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#ED9E0E",
                    margin: 0,
                    cursor: openPrice ? "pointer" : "default",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    if (openPrice) e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseOut={(e) => {
                    if (openPrice) e.currentTarget.style.opacity = "1";
                  }}
                >
                  {openPrice || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Position Details */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Lots, Units, Risk Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px",
              backgroundColor:
                theme[0] === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.03)",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: width <= 600 ? "9px" : "10px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  margin: "0 0 4px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Lots
              </p>
              <p
                onClick={() => {
                  if (lots) {
                    navigator.clipboard.writeText(lots.toString());
                    showCopyNotification(`Copied ${lots} Lots`);
                  }
                }}
                style={{
                  fontSize: width <= 600 ? "16px" : "18px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                  margin: 0,
                  cursor: "pointer",
                  transition: "opacity 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = "0.7";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {lots || "0.000"}
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: width <= 600 ? "9px" : "10px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  margin: "0 0 4px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Units
              </p>
              <p
                onClick={() => {
                  if (units) {
                    const cleanUnits = units.toString().replace(/,/g, '');
                    navigator.clipboard.writeText(cleanUnits);
                    showCopyNotification(`Copied ${cleanUnits} Units`);
                  }
                }}
                style={{
                  fontSize: width <= 600 ? "16px" : "18px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                  margin: 0,
                  cursor: "pointer",
                  transition: "opacity 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = "0.7";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {units || "0"}
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: width <= 600 ? "9px" : "10px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  margin: "0 0 4px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Risk
              </p>
              <p
                style={{
                  fontSize: width <= 600 ? "16px" : "18px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  onClick={() => {
                    if (moneyRisk) {
                      navigator.clipboard.writeText(moneyRisk.toString());
                      const symbol = depositCurrency === "gbp" ? moneySymbol[1] : moneySymbol[0];
                      showCopyNotification(`Copied ${symbol}${moneyRisk}`);
                    }
                  }}
                  style={{
                    cursor: moneyRisk ? "pointer" : "default",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    if (moneyRisk) e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseOut={(e) => {
                    if (moneyRisk) e.currentTarget.style.opacity = "1";
                  }}
                >
                  {depositCurrency === "gbp" ? moneySymbol[1] : moneySymbol[0]}
                  {moneyRisk || "0.00"}
                </span>
              </p>
            </div>
          </div>

          {/* Additional Details */}
          <div
            style={{
              backgroundColor:
                theme[0] === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.03)",
              borderRadius: "8px",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                Balance:
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                }}
              >
                {depositCurrency === "gbp" ? moneySymbol[1] : moneySymbol[0]}
                {accountBalance || "0.00"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                Deposit:
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                }}
              >
                {depositCurrency || "USD"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                {exchangeLabel ? `${exchangeLabel}:` : "Rate:"}
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#ED9E0E",
                }}
              >
                {exchangeRate || "—"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                Contract:
              </span>
              {showContractDropdown ? (
                customContractSize ? (
                  <input
                    autoFocus
                    onBlur={() => {
                      if (!contractSize) {
                        setCustomContractSize(false);
                      } else {
                        saveContractSize(Number(contractSize));
                        setShowContractDropdown(false);
                      }
                    }}
                    type="number"
                    style={{
                      width: "80px",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#ED9E0E",
                      backgroundColor: "transparent",
                      border: "none",
                      borderBottom: `1px solid #ED9E0E`,
                      outline: "none",
                      padding: "2px 4px",
                      textAlign: "right",
                      fontFamily: "inherit",
                    }}
                    value={contractSize}
                    onChange={(e) => {
                      setContractSize(e.target.value);
                    }}
                  />
                ) : (
                  <select
                    autoFocus
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#ED9E0E",
                      backgroundColor: theme[0] === "dark" ? "#1a1a1a" : "#ffffff",
                      border: `1px solid #ED9E0E`,
                      borderRadius: "4px",
                      outline: "none",
                      padding: "2px 6px",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                    value={contractSize}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setContractSize(newValue);
                      if (newValue === 0) {
                        setCustomContractSize(true);
                      } else {
                        saveContractSize(newValue);
                        setShowContractDropdown(false);
                      }
                    }}
                  >
                    <option value={100000}>100,000</option>
                    <option value={10000}>10,000</option>
                    <option value={1000}>1,000</option>
                    <option value={100}>100</option>
                    <option value={0}>Custom</option>
                  </select>
                )
              ) : (
                <Tooltip title="Long-press to edit contract size" arrow>
                  <span
                    onMouseDown={handleContractMouseDown}
                    onMouseUp={handleContractMouseUp}
                    onMouseLeave={handleContractMouseLeave}
                    onTouchStart={handleContractMouseDown}
                    onTouchEnd={handleContractMouseUp}
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#ED9E0E",
                      cursor: "pointer",
                      transition: "opacity 0.2s ease",
                      touchAction: "manipulation",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.opacity = "0.7";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                  >
                    {contractSize?.toLocaleString() || "—"}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionVisualizer;
